import React, { FC, memo, useCallback } from 'react';
import { useSelector } from 'react-redux';

import { BiggestDiscountCarousel } from '@browsing/biggest-discount';
import { OpenCampaignCard, OpenCampaignCardAspectRatio } from '@browsing/campaign';
import { TopPicks4U } from '@browsing/top-picks-4U';
import { mapFashionPreference } from '@browsing/utils';

import { Campaign } from '@shared/types/mylounge';

import {
    NEW_DEALS_MOBILE_FIXED_POSITION,
    SHOWSTOPPER_FIXED_POSITION,
    BIGGGEST_DISCOUNT_POSITION_MOBILE,
    SEMANTIC_ARTICLE_CAROUSEL_FIXED_POSITION,
} from '@client/config/constants';

import { getCustomerFashionPreferences, getGender } from '@client/redux/modules/customer';
import { CustomerGender } from '@client/components/mylounge/campaigns/grid/constants';
import { CategoryTabIds } from '@shared/constants';
import { getEAOpenCampaigns, getTabs } from '@client/redux/modules/mylounge';
import { readSelectedFashionPreference } from '@client/helpers';
import * as S from './mobile-campaigns.styles';
import { ShowstopperMobileContainer } from '../components/showstopper.styles';
import { PlusExclusiveCampaign, usePlusExclusiveCampaignPosition } from '../../plus';
import { NewDealsCarousel } from '../../new-deals-today';
import { SemanticArticleCarousel } from '../../semantic-article-carousel';

interface Props {
    campaigns: Campaign[];
    campaignsStartingToday: Campaign[];
    isPlusEnabled: boolean;
    categoryTabId: CategoryTabIds;
    isNewDealsToday: boolean;
}

export const MobileCampaigns: FC<Props> = ({
    campaigns,
    campaignsStartingToday,
    categoryTabId,
    isPlusEnabled,
    isNewDealsToday,
}) => {
    const gender = useSelector(getGender)?.toUpperCase?.() as CustomerGender;
    const profileFashionPreference = mapFashionPreference(
        useSelector(getCustomerFashionPreferences)?.[0],
    );
    const selectedFashionPreference = readSelectedFashionPreference();
    const earlyAccessOpenCampaigns = useSelector(getEAOpenCampaigns);
    const tabs = useSelector(getTabs);
    const hasPlusOffersTab = tabs.some(tab => tab.event === 'PLUS_OFFER_EVENT');

    const plusExclusiveCampaignPosition = usePlusExclusiveCampaignPosition({
        columnCount: 1, // On mobile columnCount is always 1
        offset: 0, // We don't apply offset for mobile because we render all early access campaigns separately at the top of all campaigns
    });

    const injectInterstitialComponent = useCallback(
        (index: number) => {
            if (plusExclusiveCampaignPosition === index) {
                return (
                    <PlusExclusiveCampaign
                        position={`${index}|1` /* for tracking purposes */}
                        aspectRatio={OpenCampaignCardAspectRatio.Mobile}
                        withMobileSpacing
                    />
                );
            }
        },
        [plusExclusiveCampaignPosition],
    );

    if (!campaigns.length) {
        // TODO: return valid translated text
        return null;
    }

    return (
        <S.Container id="campaigns-open">
            {isPlusEnabled &&
                categoryTabId === CategoryTabIds.ALL &&
                !hasPlusOffersTab &&
                earlyAccessOpenCampaigns?.map((campaign, index) => (
                    <OpenCampaignCard
                        key={campaign.campaignId}
                        aspectRatio={OpenCampaignCardAspectRatio.Mobile}
                        campaign={campaign}
                        isPlusEnabled={isPlusEnabled}
                        position={`${index + 1}|1`}
                        withMobileSpacing
                    />
                ))}
            {campaigns.map((campaign, index) => {
                return (
                    <React.Fragment key={campaign.campaignId}>
                        {index === NEW_DEALS_MOBILE_FIXED_POSITION && isNewDealsToday && (
                            <NewDealsCarousel campaignsStartingToday={campaignsStartingToday} />
                        )}

                        {index === SHOWSTOPPER_FIXED_POSITION &&
                            categoryTabId === CategoryTabIds.ALL && (
                                <ShowstopperMobileContainer>
                                    <TopPicks4U
                                        isMobileScreen
                                        gender={gender}
                                        profileFashionPreference={profileFashionPreference}
                                        selectedFashionPreference={selectedFashionPreference}
                                    />
                                </ShowstopperMobileContainer>
                            )}

                        {index === BIGGGEST_DISCOUNT_POSITION_MOBILE &&
                            categoryTabId === CategoryTabIds.ALL && (
                                <BiggestDiscountCarousel
                                    profileFashionPreference={profileFashionPreference}
                                    selectedFashionPreference={selectedFashionPreference}
                                />
                            )}

                        {injectInterstitialComponent && injectInterstitialComponent(index)}

                        <OpenCampaignCard
                            key={campaign.campaignId}
                            aspectRatio={OpenCampaignCardAspectRatio.Mobile}
                            campaign={campaign}
                            isPlusEnabled={isPlusEnabled}
                            position={`${index}|1`}
                            withMobileSpacing
                        />

                        {index === SEMANTIC_ARTICLE_CAROUSEL_FIXED_POSITION &&
                            categoryTabId === CategoryTabIds.ALL && <SemanticArticleCarousel />}
                    </React.Fragment>
                );
            })}
        </S.Container>
    );
};

export default memo(MobileCampaigns);
